import { logger } from "@timeghost/utils/logger";
import { z } from "zod";

const DEFAULT_LOG_LEVEL = 1;
const DEFAULT_GRAPH_SCOPE = "User.Read";
const DEFAULT_LOGIN_SCOPES = "openid,profile,offline_access,User.Read";

const resolveBrowserOrigin = (): string => {
	if (typeof window === "undefined") {
		return "/";
	}

	return window.location.origin;
};

export const environmentSchema = z
	.object({
		/**
		 * Public app origin for this deploy.
		 * Hosts on AAD allowlist: https://web.timeghost.io | https://web-beta.timeghost.io |
		 * https://web-dev.timeghost.io | https://web-next.timeghost.io
		 * Defaults MSAL redirect/logout URIs when those vars are unset.
		 */
		VITE_BASE_URL: z
			.url()
			.nullish()
			.transform((value) => value ?? resolveBrowserOrigin()),
		VITE_AZURE_AUTHORITY: z.url({ error: "VITE_AZURE_AUTHORITY is required" }),
		VITE_AZURE_CLIENT_ID: z.string().min(1, "VITE_AZURE_CLIENT_ID is required"),
		VITE_AZURE_POST_LOGOUT_REDIRECT_URI: z
			.string()
			.nullish()
			.transform((value) => value ?? resolveBrowserOrigin()),
		VITE_AZURE_REDIRECT_URI: z
			.string()
			.nullish()
			.transform((value) => value ?? resolveBrowserOrigin()),
		VITE_AZURE_SCOPES: z.string().default(DEFAULT_LOGIN_SCOPES),
		VITE_AZURE_API_SCOPE: z
			.string()
			.min(1, "VITE_AZURE_API_SCOPE is required")
			.describe("API resource scope for Authorization Bearer token"),
		VITE_AZURE_GRAPH_SCOPE: z.string().default(DEFAULT_GRAPH_SCOPE),
		VITE_LOG_LEVEL: z.coerce.number().int().min(0).default(DEFAULT_LOG_LEVEL),
		VITE_API_URL: z
			.url("VITE_API_URL must be a valid URL")
			.nullish()
			.transform((value) => value ?? "https://timeghost-dev.azurewebsites.net/api"),
		VITE_PADDLE_CLIENT_TOKEN: z.string().default(""),
		VITE_PADDLE_PRICE_MONTHLY: z.string().default(""),
		VITE_PADDLE_PRICE_YEARLY: z.string().default(""),
		/** "true" / "1" forces sandbox; unset uses DEV sandbox / PROD live. */
		VITE_PADDLE_SANDBOX: z.coerce
			.boolean()
			.optional()
			.default(Boolean(import.meta.env.DEV)),
		/**
		 * Azure Marketplace / AppSource fulfillment listing.
		 * - Key unset: Angular-parity defaults (dev portal / prod AppSource).
		 * - Key set to empty / off / false / 0 / none: hide Microsoft payment option.
		 * - Non-empty URL: use as-is.
		 */
		VITE_MICROSOFT_FULFILLMENT_URL: z
			.string()
			.nullish()
			.transform((value) => value ?? ""),
	})
	.transform((values) => {
		const keyPresent = Object.hasOwn(import.meta.env, "VITE_MICROSOFT_FULFILLMENT_URL");
		const raw = values.VITE_MICROSOFT_FULFILLMENT_URL;
		const trimmed = typeof raw === "string" ? raw.trim() : "";
		const hideTokens = new Set(["", "off", "false", "0", "none", "null"]);

		let fulfillmentUrl = "";
		if (keyPresent) {
			fulfillmentUrl = hideTokens.has(trimmed.toLowerCase()) ? "" : trimmed;
		} else {
			fulfillmentUrl = import.meta.env.PROD
				? "https://appsource.microsoft.com/de-de/product/web-apps/schgesellschaftfrprozessmanagementmbh1592311158227.timeghost_fulfillment?tab=Overview"
				: "https://portal.azure.com/#create/schgesellschaftfrprozessmanagementmbh1592311158227.timeghost_io/preview";
		}

		const baseUrl = values.VITE_BASE_URL?.replace(/\/$/, "") || resolveBrowserOrigin();
		const redirectUri = values.VITE_AZURE_REDIRECT_URI?.trim() || baseUrl;
		const postLogoutRedirectUri = values.VITE_AZURE_POST_LOGOUT_REDIRECT_URI?.trim() || baseUrl;

		return {
			...values,
			VITE_BASE_URL: baseUrl,
			VITE_AZURE_REDIRECT_URI: redirectUri,
			VITE_AZURE_POST_LOGOUT_REDIRECT_URI: postLogoutRedirectUri,
			VITE_MICROSOFT_FULFILLMENT_URL: fulfillmentUrl,
		};
	});

export type Environment = z.infer<typeof environmentSchema>;

const { data, error } = environmentSchema.safeParse(import.meta.env);

if (error) {
	logger.debug({
		env: process.env,
		meta: import.meta.env,
	});
	throw new Error(`Invalid environment variables: ${error.message}`);
}
if (import.meta.env.DEV)
	logger.debug({
		env: import.meta.env,
	});

export const environment: Environment = data;
